import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useGetOrderDetailsQuery } from "../api/orderApi";
import { useOrderProcessing } from "../hooks/useOrderProcessing";
import Icon from "../../../../common/components/Icon/Icon";
import OrderPageSkeleton from "./OrderPageSkeleton";
import OrderEmailForm from "./OrderEmailForm";
import OrderActions from "./OrderActions";
import OrderHeader from "./OrderHeader";
import OrderSummary from "./OrderSummary";
import OrderItemsSummary from "./OrderItemsSummary";
import OrderAddressCard from "./OrderAddressCard";
import OrderBacsDetails from "./OrderBacsDetails";
import OrderTotals from "./OrderTotals";
import OrderAgreementsSnapshot from "./OrderAgreementsSnapshot";
import DeliveryEstimateDisplay from "../../product/components/common/DeliveryEstimateDisplay";
import CustomerNoteDisplay from "./CustomerNoteDisplay";

const STORAGE_KEY_EMAIL = "wc_customer_billing_email";

const OrderPage: React.FC = () => {
  const { t } = useTranslation();

  // Extract Order Info from URL
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const orderIdStr = pathParts[pathParts.length - 1];
  const orderId = parseInt(orderIdStr, 10);
  const params = new URLSearchParams(window.location.search);
  const orderKey = params.get("key");

  // Detect context (origin=history is sent when coming from User Profile)
  const isHistoryView = params.get("origin") === "history";

  // Check Auth Status
  const isLoggedIn = window.commonVariables?.isLoggedIn || false;

  // Manage Email State for Guest Verification
  const [billingEmail, setBillingEmail] = useState<string | null>(() => {
    return sessionStorage.getItem(STORAGE_KEY_EMAIL);
  });

  const handleEmailSubmit = (email: string) => {
    sessionStorage.setItem(STORAGE_KEY_EMAIL, email);
    setBillingEmail(email);
  };

  const handleResetEmail = () => {
    setBillingEmail(null);
    sessionStorage.removeItem(STORAGE_KEY_EMAIL);
  };

  // Fetch Order Data
  const hasRequiredParams = orderId && orderKey;
  const canFetch = !!(hasRequiredParams && (isLoggedIn || billingEmail));

  const {
    data: order,
    isLoading,
    isError,
    error,
    isSuccess: isOrderLoaded,
  } = useGetOrderDetailsQuery(
    {
      orderId,
      orderKey: orderKey!,
      email: isLoggedIn ? "" : billingEmail || "",
    },
    {
      skip: !canFetch,
    },
  );

  // --- CUSTOM HOOK: HANDLE POST-PURCHASE LOGIC (Clear Cart & Tracking) ---
  useOrderProcessing(order, isOrderLoaded, isHistoryView);

  // Handle Permission Errors
  useEffect(() => {
    if (isError && error && "status" in error) {
      if (error.status === 401 || error.status === 403) {
        if (!isLoggedIn) {
          // Check for the specific error code from backend
          const errorData = error.data as any;
          const isInvalidEmail =
            errorData?.code === "woocommerce_rest_invalid_billing_email";

          if (isInvalidEmail) {
            toast.error(
              t(
                "orderPage.errorInvalidEmail",
                "The email address provided does not match the order.",
              ),
            );
          } else {
            // Fallback generic error if needed
            toast.error(
              t("orderPage.errorAccessDenied", "Access denied. Please retry."),
            );
          }

          // Reset the email state to show the form again
          handleResetEmail();
        }
      }
    }
  }, [isError, error, isLoggedIn, t]);

  // --- Render Logic ---

  if (isLoading) {
    return <OrderPageSkeleton />;
  }

  if (!isLoggedIn && !billingEmail) {
    return <OrderEmailForm onSubmit={handleEmailSubmit} />;
  }

  if (isError || !order) {
    return (
      <div className="order-page__header">
        <Icon
          iconName="x-mark"
          className="icon--stroke-thickest"
          style={{ color: "var(--color-danger)" }}
        />
        <h1 className="order-page__title">
          {t("orderPage.errorTitle", "Could Not Find Order")}
        </h1>
        <p className="order-page__subtitle">
          {isLoggedIn
            ? t(
                "orderPage.errorAccessDenied",
                "You do not have permission to view this order.",
              )
            : t(
                "orderPage.errorMessage",
                "We couldn't retrieve your order details.",
              )}
        </p>
      </div>
    );
  }

  return (
    <div className="order-page">
      <OrderActions />

      <OrderHeader order={order} isHistoryView={isHistoryView} />

      <OrderSummary order={order} />

      <CustomerNoteDisplay note={order.customerNote} />

      {/* Conditionally render delivery estimate only if shipping is needed */}
      {order.needsShipping && (
        <DeliveryEstimateDisplay
          estimate={order.deliveryEstimation}
          className="order-page__delivery-estimate"
        />
      )}

      <OrderBacsDetails order={order} />

      <OrderItemsSummary order={order} />

      <div className="order-page__details-grid">
        <OrderAddressCard
          title={t("orderReview.billingAddress", "Billing Address")}
          address={order.billingAddress}
          type="billing"
        />
        {order.needsShipping && (
          <OrderAddressCard
            title={t("orderReview.shippingAddress", "Shipping Address")}
            address={order.shippingAddress}
            type="shipping"
          />
        )}
      </div>

      <OrderTotals order={order} />

      <OrderAgreementsSnapshot snapshot={order.legalAgreementsSnapshot} />
    </div>
  );
};

export default OrderPage;
